<template>
  <VRow class="h-100">
    <VCol cols="12" class="d-flex flex-column">
      <VacancyStepTitle
        v-if="vacancy"
        :progress="100"
        :vacancy="vacancy"
        :step="4"
      >
        {{ $t('step') }}
      </VacancyStepTitle>

      <Preloader
        v-if="loading"
        :text="$t('pleaseWaitAiLoaderTextVoice')"
        class="flex-grow-1"
      />
    </VCol>
  </VRow>
</template>

<script>
import VacancyStepTitle from '@/views/Vacancy/VacancyStepTitle';
import Preloader from '@/components/Preloader.vue';
import { wizardSteps, vacancyStatus } from '@/enums';
import Wizard from '@/orm/models/Wizard/Wizard';
import useVacancies from '@/compositions/useVacancies';
import {
  ref, computed, watch, onMounted, onBeforeUnmount,
} from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';

export default {
  name: 'VacancyStepPublish',
  components: {
    VacancyStepTitle,
    Preloader,
  },
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
  },
  setup(props, { root }) {
    const { fetchById, getVacancyById } = useVacancies();
    const loading = ref(true);
    const fetchTimeout = ref(null);
    const isMounted = ref(true);

    const { t } = useI18n();

    const vacancy = computed(() => getVacancyById(props.vacancyId));

    const fetchVacancy = async () => {
      if (!isMounted.value) {
        return;
      }
      await fetchById(props.vacancyId);
    };

    const fetchVacancyByIdWhenLoading = async () => {
      if (!loading.value || !isMounted.value) {
        return;
      }
      await fetchVacancy();
    };

    const fetchVacancyWhileLoading = async () => {
      if (!loading.value || !isMounted.value) {
        return;
      }
      fetchTimeout.value = setTimeout(async () => {
        if (!isMounted.value) {
          return;
        }
        await fetchVacancyByIdWhenLoading();
        if (loading.value && isMounted.value) {
          await fetchVacancyWhileLoading();
        }
      }, 10000);
    };

    const enableLoader = () => {
      if (loading.value && isMounted.value) {
        fetchVacancyWhileLoading();
      }
    };

    const disableLoader = () => {
      loading.value = false;
      isMounted.value = false;
      if (fetchTimeout.value) {
        clearTimeout(fetchTimeout.value);
        fetchTimeout.value = null;
      }
    };

    const prepare = async () => {
      if (!isMounted.value) {
        return;
      }
      Wizard
        .setVacancyId(props.vacancyId)
        .setWizardStep(wizardSteps.PUBLISH);

      await Wizard.saveWizardState();

      if (!isMounted.value) {
        return;
      }

      await fetchById(props.vacancyId);

      if (!isMounted.value) {
        return;
      }

      loading.value = Boolean(vacancy.value?.isAiInterviewCompiling || vacancy.value?.isAiInterviewRecompiling);
      if (loading.value && isMounted.value) {
        enableLoader();
      }

      if (vacancy.value?.status === vacancyStatus.ACTIVE && isMounted.value) {
        root.$router.push({
          name: 'candidates',
          params: { id: props.vacancyId },
        });
      }
    };

    watch(vacancy, (newVacancy) => {
      if (!newVacancy || !isMounted.value) return;
      const compiling = Boolean(newVacancy?.isAiInterviewCompiling || newVacancy?.isAiInterviewRecompiling);
      const isCompiled = Boolean(newVacancy?.isAiInterviewCompiled);

      if (isCompiled && !compiling && loading.value) {
        if (newVacancy?.status === vacancyStatus.ACTIVE && isMounted.value) {
          root.$toast.success(t('vacancyCreated'));
          root.$router.push({
            name: 'candidates',
            params: { id: props.vacancyId },
          });
        }

        disableLoader();
      }
    }, { deep: true });

    onMounted(async () => {
      await prepare();
    });

    onBeforeUnmount(() => {
      disableLoader();
    });

    return {
      loading,
      vacancy,
      fetchById,
      getVacancyById,
    };
  },
};
</script>

<style lang="scss" scoped>
.h-100 {
  height: 100%;
}
</style>

<i18n locale="en" lang="yaml">
  step: "Publish"
  publish: Publish
  pleaseWaitAiLoaderTextVoice: 'Artificial intelligence is in the process of voicing questions for your job.
    We expect it to take 3-5 minutes, please wait... If necessary, you can go to other sections.
    The job will be available in the jobs section after completion of the preparation process'
  vacancyCreated: Job created
</i18n>

<i18n locale="ru" lang="yaml">
  step: "Публикация"
  publish: Опубликовать
  pleaseWaitAiLoaderTextVoice: 'Искусственный интеллект в процессе озвучивания вопросов для вашей вакансии.
    Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости вы можете перейти в другие разделы.
    Вакансия будет доступна в разделе вакансий после завершения процесса подготовки.'
  vacancyCreated: Вакансия создана
</i18n>
